import { useFieldArray, useFormContext, UseFormReturn, useWatch } from "react-hook-form";
import { CustomCotationQuoteFormData } from "../customQuotationQuote.form";
import { Button } from "@/components/ui/button";
import clsx from "clsx";
import { useGetCompany } from "@/api/company.api";
import { getTvaApplications, isInEuVatTerritory } from "@/../../shared/helpers/tvaApplicability";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  GetAllCompanyDto,
  GetCustomsV1Response,
  GetOneExpeditionBoResponseDto,
} from "@/types/api/api-types";
import { useGetGlobalParameters } from "@/api/globalParameters.api";
import { ControlledUnifiedInput } from "@/components/ui/customs/controlled-unified-input";
import { ControlledCustomsSelect } from "@/components/ui/customs/controlled-customs-select";
import { Trash2 } from "lucide-react";
import { ControlledCheckbox } from "@/components/ui/customs/controlled-checkbox";
import { useAtomValue } from "jotai";
import { userAtom } from "@/stores/authStore";
import { RolesEnum } from "@/constants/enums/roles.enum";

const TVA_RATE = 0.2;
const INSURANCE_FLAT_FEE = 5;
const INSURANCE_FLAT_FEE_THRESHOLD = 500;

type Props = {
  isMultipleExpedition: boolean;
  processStatus: string;
  expedition: GetOneExpeditionBoResponseDto;
  company: GetAllCompanyDto;
  customs: GetCustomsV1Response;
};

export const InfoQuote = ({
  isMultipleExpedition,
  processStatus,
  expedition,
  company,
  customs,
}: Props) => {
  const { t } = useTranslation("boQuotations");
  const methods = useFormContext<CustomCotationQuoteFormData>();
  const { data: billingCompany } = useGetCompany();
  const { data: globalParameters } = useGetGlobalParameters();
  const billingEntityCountry =
    billingCompany?.billingEntities?.find((e) => e.isDefault)?.country ?? "FR";

  const insurancePercentage = company.optionInsuranceActive
    ? company.insurancePercentage
    : globalParameters?.insurancePercentage ?? 0;

  const packageExpeditionValue: number | null = expedition.customQuotation?.parcelDetail
    ? expedition.customQuotation.parcelDetail.parcelPackages.reduce(
        (sum, pkg) => sum + pkg.value,
        0,
      )
    : expedition.customQuotation?.paletteDetail
      ? expedition.customQuotation.paletteDetail.palettePackages.reduce(
          (sum, pkg) => sum + pkg.value,
          0,
        )
      : null;

  const hasInsurance = expedition.expeditionOptions?.insurance ?? false;

  const invoiceDataArray = useFieldArray({
    control: methods.control,
    name: "invoiceData",
  });

  return (
    <div className="flex flex-col gap-2 rounded-lg bg-blue-50 p-4">
      <div className="flex flex-row items-center gap-2">
        <div className="bg-brand600 rounded-full px-3 py-1 text-xl font-bold text-white">
          <p className="text-xl font-bold text-white">1</p>
        </div>
        <p className="text-xl font-bold">{t("quoteInfo.title")}</p>
      </div>
      <div className="flex flex-col gap-4">
        {invoiceDataArray.fields.map((field, index) => (
          <QuoteForm
            key={field.id}
            index={index}
            methods={methods}
            expeditionCount={invoiceDataArray.fields.length}
            remove={() => invoiceDataArray.remove(index)}
            isMultipleExpedition={isMultipleExpedition}
            billingEntityCountry={billingEntityCountry}
            packageExpeditionValue={packageExpeditionValue}
            hasInsurance={hasInsurance}
            insurancePercentage={insurancePercentage}
            customs={customs}
          />
        ))}
      </div>
      <Button
        variant={"outline"}
        type="button"
        className={clsx(
          (!isMultipleExpedition || processStatus !== "PENDING_QUOTE") && "hidden",
          "w-fit",
        )}
        onClick={() => {
          invoiceDataArray.append({
            client: "",
            customId: "",
            reference: "",
            merchandiseValue: 0,
            transportHt: undefined as unknown as number,
            packaging: undefined as unknown as number,
            packagingProtection: undefined as unknown as number,
            taxes: undefined as unknown as number,
            insurance: undefined as unknown as number,
            tva: undefined as unknown as number,
            insuranceTvaOverride: false,
          });
        }}
      >
        {t("quoteInfo.addQuote")}
      </Button>
    </div>
  );
};

const QuoteForm = ({
  index,
  methods,
  expeditionCount,
  remove,
  isMultipleExpedition,
  billingEntityCountry,
  packageExpeditionValue,
  hasInsurance,
  insurancePercentage,
  customs,
}: {
  index: number;
  methods: UseFormReturn<CustomCotationQuoteFormData>;
  expeditionCount: number;
  remove: () => void;
  isMultipleExpedition: boolean;
  billingEntityCountry: string;
  packageExpeditionValue: number | null;
  hasInsurance: boolean;
  insurancePercentage: number;
  customs: GetCustomsV1Response;
}) => {
  const { t } = useTranslation("boQuotations");
  const customId = useWatch({ control: methods.control, name: `invoiceData.${index}.customId` });
  const transportHt = methods.watch(`invoiceData.${index}.transportHt`) || 0;
  const packaging = methods.watch(`invoiceData.${index}.packaging`) || 0;
  const packagingProtection = methods.watch(`invoiceData.${index}.packagingProtection`) || 0;
  const insurance = methods.watch(`invoiceData.${index}.insurance`) || 0;
  const merchandiseValue = methods.watch(`invoiceData.${index}.merchandiseValue`) || 0;

  const user = useAtomValue(userAtom);
  const isSuperAdmin = user?.role === RolesEnum.SUPERADMIN;
  const isManualOverride =
    useWatch({ control: methods.control, name: `invoiceData.${index}.insuranceTvaOverride` }) ??
    false;

  const custom = customs.find((custom) => custom.id === customId);
  const countryCode = custom?.flagCode;

  const isDestinationEU = countryCode ? isInEuVatTerritory(countryCode) : false;
  const tvaApplications = getTvaApplications(billingEntityCountry, isDestinationEU);

  const expeditionValueToUse = packageExpeditionValue ?? merchandiseValue;

  const calculatedInsurance =
    expeditionValueToUse !== undefined
      ? hasInsurance
        ? parseFloat(
            (expeditionValueToUse <= INSURANCE_FLAT_FEE_THRESHOLD
              ? INSURANCE_FLAT_FEE
              : (expeditionValueToUse * insurancePercentage) / 100
            ).toFixed(2),
          )
        : 0
      : null;

  const calculatedTva = parseFloat(
    (
      (tvaApplications.transport ? (transportHt + packaging) * TVA_RATE : 0) +
      (tvaApplications.membranes ? packagingProtection * TVA_RATE : 0) +
      (tvaApplications.insurance ? insurance * TVA_RATE : 0)
    ).toFixed(2),
  );

  useEffect(() => {
    if (isManualOverride) return;
    if (calculatedInsurance !== null) {
      methods.setValue(`invoiceData.${index}.insurance`, calculatedInsurance);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calculatedInsurance, isManualOverride]);

  useEffect(() => {
    if (isManualOverride) return;
    methods.setValue(`invoiceData.${index}.tva`, calculatedTva);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calculatedTva, isManualOverride]);

  return (
    <div className="flex flex-col gap-2 rounded-lg border border-gray-300 bg-gray-50 p-2 shadow-sm">
      <div className="flex w-full flex-row items-center justify-between gap-2">
        <p className="text-base font-bold">
          {t("quoteInfo.quoteCounter", { number: index + 1, total: expeditionCount })}
        </p>
        <Button
          variant={"destructive"}
          type="button"
          onClick={remove}
          disabled={expeditionCount < 2}
          className={clsx(!isMultipleExpedition && "hidden")}
        >
          <Trash2 />
        </Button>
      </div>
      <h3 className={clsx("text-sm font-bold", isMultipleExpedition ? "block" : "hidden")}>
        {t("quoteInfo.deliveryDataTitle")}
      </h3>
      <div className={clsx("grid w-full grid-cols-2 gap-2")}>
        <ControlledUnifiedInput
          control={methods.control}
          name={`invoiceData.${index}.client`}
          label={t("quoteInfo.clientLabel")}
          inputType="text"
          placeholder={t("quoteInfo.clientPlaceholder")}
        />
        <ControlledCustomsSelect
          customs={customs}
          control={methods.control}
          name={`invoiceData.${index}.customId`}
          label={t("quoteInfo.destinationCountryLabel")}
          placeholder={t("quoteInfo.destinationCountryPlaceholder")}
        />
        <ControlledUnifiedInput
          control={methods.control}
          name={`invoiceData.${index}.reference`}
          label={t("quoteInfo.referenceLabel")}
          inputType="text"
          placeholder={t("quoteInfo.referencePlaceholder")}
        />
      </div>
      <h3 className="text-sm font-bold">{t("quoteInfo.billingDataTitle")}</h3>
      <div className="grid w-full grid-cols-2 gap-2">
        <ControlledUnifiedInput
          control={methods.control}
          name={`invoiceData.${index}.transportHt`}
          label={t("quoteInfo.transportLabel")}
          rightElement={<p>€</p>}
          inputType="float"
          maxDecimals={2}
          placeholder={t("quoteInfo.transportPlaceholder")}
        />
        <ControlledUnifiedInput
          control={methods.control}
          name={`invoiceData.${index}.packaging`}
          label={t("quoteInfo.packagingLabel")}
          rightElement={<p>€</p>}
          inputType="float"
          maxDecimals={2}
          placeholder={t("quoteInfo.packagingPlaceholder")}
        />
        <ControlledUnifiedInput
          control={methods.control}
          name={`invoiceData.${index}.packagingProtection`}
          label={t("quoteInfo.packagingProtectionLabel")}
          rightElement={<p>€</p>}
          inputType="float"
          maxDecimals={2}
        />
        <ControlledUnifiedInput
          control={methods.control}
          name={`invoiceData.${index}.taxes`}
          label={t("quoteInfo.taxesLabel")}
          rightElement={<p>€</p>}
          inputType="float"
          maxDecimals={2}
        />
        <ControlledUnifiedInput
          control={methods.control}
          name={`invoiceData.${index}.merchandiseValue`}
          label={t("quoteInfo.merchandiseValueLabel")}
          rightElement={<p>€</p>}
          inputType="float"
          maxDecimals={2}
          disabled={!!packageExpeditionValue}
        />
        <ControlledUnifiedInput
          control={methods.control}
          name={`invoiceData.${index}.insurance`}
          label={t("quoteInfo.insuranceLabel")}
          rightElement={<p>€</p>}
          inputType="float"
          maxDecimals={2}
          disabled={!isManualOverride}
        />
        <ControlledUnifiedInput
          control={methods.control}
          name={`invoiceData.${index}.tva`}
          label={t("quoteInfo.vatLabel")}
          rightElement={<p>€</p>}
          inputType="float"
          maxDecimals={2}
          disabled={!isManualOverride}
        />
      </div>
      {isSuperAdmin && (
        <ControlledCheckbox
          control={methods.control}
          name={`invoiceData.${index}.insuranceTvaOverride`}
          label={t("quoteInfo.manualOverrideLabel")}
        />
      )}
      <div className="mt-2 flex flex-row items-center gap-2">
        <p className="text-xl font-bold">{t("quoteInfo.total")}</p>
        <p className="text-xl font-bold">
          {Number(
            (methods.watch(`invoiceData.${index}.transportHt`) || 0) +
              (methods.watch(`invoiceData.${index}.packaging`) || 0) +
              (methods.watch(`invoiceData.${index}.packagingProtection`) || 0) +
              (methods.watch(`invoiceData.${index}.taxes`) || 0) +
              (methods.watch(`invoiceData.${index}.insurance`) || 0) +
              (methods.watch(`invoiceData.${index}.tva`) || 0),
          ).toFixed(2)}
          €
        </p>
      </div>
    </div>
  );
};
